import { getUnixTime } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "react-query";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { BookingPost } from "../../components/BookingPost";
import { CTA } from "../../components/CTA";
import { DateRangePicker } from "../../components/DateRangePicker";
import { Dropdown } from "../../components/Dropdown";
import { Flex } from "../../components/Flex";
import { H1 } from "../../components/Heading";
import Loading from "../../components/Loading";
import MasonryList from "../../components/MasonryList";
import { Text } from "../../components/Text";
import { ContentIcon } from "../../components/icons/ContentIcon";
import { ListingsIcon } from "../../components/icons/ListingsIcon";
import { LocationsIcon } from "../../components/icons/LocationsIcon";
import { MOBILE_BREAKPOINT } from "../../config";
import {
  BookingPostType,
  BookingPostsSortBy,
  BrandContentDocument,
  BrandContentQuery,
  useBrandGroupListingsQuery,
  useBrandGroupLocationsQuery,
  useBrandListingsQuery,
  useBrandLocationsQuery,
  useGetBrandSocialsQuery,
} from "../../graphql/generated";
import { queryKeys } from "../../graphql/queryKeys";
import useGqlClient from "../../hooks/useGqlClient";
import { authSelectors } from "../../store/auth/selector";
import { styled } from "../../styles";

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 0 ${(p) => p.theme.spacing.xl};
  box-sizing: border-box;
  text-align: left;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 0 ${(p) => p.theme.spacing.l};
  }
`;

export interface MatchParams {
  page: string;
}

export enum DateFilterOptions {
  AllTime = "allTime",
  Last30Days = "last30Days",
  Last7Days = "last7Days",
  ThisMonth = "thisMonth",
  LastMonth = "lastMonth",
}

export interface FilterOption {
  label: string;
  id: string | BookingPostType;
}

export const contentTypes: FilterOption[] = [
  { id: BookingPostType.BookingPostTypeFeed, label: "Instagram Posts" },
  { id: BookingPostType.BookingPostTypeReel, label: "Instagram Reels" },
  { id: BookingPostType.BookingPostTypeStory, label: "Instagram Stories" },
  { id: BookingPostType.BookingPostTypeTiktok, label: "TikTok" },
];

const HeaderWrap = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin: 0 0 ${(p) => p.theme.spacing.l};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${(p) => p.theme.spacing.m};
  }
`;

export const FilterGrid = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: ${(p) => p.theme.spacing.s};
`;

// Helper functions to serialize/deserialize URL params
const serializeFilters = (
  startDate: Date | null,
  endDate: Date | null,
  // searchQuery: string,
  selectedListings: string[],
  selectedLocations: string[],
  selectedContentType: BookingPostType[],
  allListings: FilterOption[],
  allLocations: FilterOption[]
) => {
  const params = new URLSearchParams();

  // Only add to URL if filters are actually applied (not default "all")
  if (startDate) params.set("startDate", startDate.toISOString());
  if (endDate) params.set("endDate", endDate.toISOString());
  // if (searchQuery) params.set("search", searchQuery);

  // Only add listings if not all are selected
  const allListingIds = allListings.map((l) => l.id);
  const hasAllListings =
    allListingIds.length > 0 &&
    allListingIds.length === selectedListings.length &&
    allListingIds.every((id) => selectedListings.includes(id));
  if (!hasAllListings && selectedListings.length > 0) {
    params.set("listings", selectedListings.join(","));
  }

  // Only add locations if not all are selected
  const allLocationIds = allLocations.map((l) => l.id);
  const hasAllLocations =
    allLocationIds.length > 0 &&
    allLocationIds.length === selectedLocations.length &&
    allLocationIds.every((id) => selectedLocations.includes(id));
  if (!hasAllLocations && selectedLocations.length > 0) {
    params.set("locations", selectedLocations.join(","));
  }

  // Only add contentTypes if not all are selected
  const allContentTypes = contentTypes.map((c) => c.id as BookingPostType);
  const hasAllContentTypes = allContentTypes.every((type) =>
    selectedContentType.includes(type)
  );
  if (!hasAllContentTypes && selectedContentType.length > 0) {
    params.set("contentTypes", selectedContentType.join(","));
  }

  return params.toString();
};

const deserializeFilters = (search: string) => {
  const params = new URLSearchParams(search);

  return {
    startDate: params.get("startDate")
      ? new Date(params.get("startDate")!)
      : null,
    endDate: params.get("endDate") ? new Date(params.get("endDate")!) : null,
    // searchQuery: params.get("search") || "",
    selectedListings: params.get("listings")?.split(",").filter(Boolean) || [],
    selectedLocations:
      params.get("locations")?.split(",").filter(Boolean) || [],
    selectedContentType: params.get("contentTypes")
      ? (params.get("contentTypes")!.split(",") as BookingPostType[])
      : contentTypes.map((c) => c.id as BookingPostType),
  };
};

export const BrandContent = () => {
  const history = useHistory();
  const location = useLocation();

  // Initialize from URL params
  const initialFilters = useMemo(
    () => deserializeFilters(location.search),
    [location.search]
  );

  const [startDate, setStartDate] = useState<Date | null>(
    initialFilters.startDate
  );
  const [endDate, setEndDate] = useState<Date | null>(initialFilters.endDate);
  // const [searchQuery, setSearchQuery] = useState<string>(
  //   initialFilters.searchQuery
  // );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const activeBrandId = useSelector(authSelectors.activeBrandId);
  const handleDateChange = ([newStartDate, newEndDate]: [
    Date | null,
    Date | null
  ]) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };
  const [listings, setListings] = useState<FilterOption[]>([]);
  const [locations, setLocations] = useState<FilterOption[]>([]);
  const [selectedListings, setSelectedListings] = useState<string[]>(
    initialFilters.selectedListings
  );
  const [selectedLocations, setSelectedLocations] = useState<string[]>(
    initialFilters.selectedLocations
  );
  const [selectedContentType, setSelectedContentType] = useState<
    BookingPostType[]
  >(initialFilters.selectedContentType);
  const activeGroupId = useSelector(authSelectors.activeGroupId);

  // Optimize content types - distinguish between all selected (undefined) vs none selected ([])
  const optimizedContentTypes = useMemo(() => {
    const allContentTypes = contentTypes.map((c) => c.id as BookingPostType);

    // None selected - send empty array for IS NULL filter
    if (selectedContentType.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected = allContentTypes.every((type) =>
      selectedContentType.includes(type)
    );
    return hasAllSelected ? undefined : selectedContentType;
  }, [selectedContentType]);

  // Optimize listings - distinguish between all selected (undefined) vs none selected ([])
  const optimizedListings = useMemo(() => {
    if (listings.length === 0) return undefined; // No data loaded yet, don't filter

    const allListingIds = listings.map((listing) => listing.id);

    // None selected - send empty array for IS NULL filter
    if (selectedListings.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected =
      allListingIds.length > 0 &&
      allListingIds.every((id) => selectedListings.includes(id));
    return hasAllSelected ? undefined : selectedListings;
  }, [listings, selectedListings]);

  // Optimize locations - distinguish between all selected (undefined) vs none selected ([])
  const optimizedLocations = useMemo(() => {
    if (locations.length === 0) return undefined; // No data loaded yet, don't filter

    const allLocationIds = locations.map((location) => location.id);

    // None selected - send empty array for IS NULL filter
    if (selectedLocations.length === 0) return [];

    // All selected - send undefined for no filter
    const hasAllSelected =
      allLocationIds.length > 0 &&
      allLocationIds.every((id) => selectedLocations.includes(id));
    return hasAllSelected ? undefined : selectedLocations;
  }, [locations, selectedLocations]);

  const client = useGqlClient();
  const { data: listingsData } = useBrandListingsQuery(
    client,
    { brandId: activeBrandId ? activeBrandId : "" },
    {
      enabled: activeBrandId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const { data: locationsData } = useBrandLocationsQuery(
    client,
    { brandId: activeBrandId ? activeBrandId : "" },
    {
      enabled: activeBrandId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const { data: groupLocationsData } = useBrandGroupLocationsQuery(
    client,
    { brandGroupId: activeGroupId ?? "" },
    {
      enabled: activeGroupId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const { data: groupListingsData } = useBrandGroupListingsQuery(
    client,
    { brandGroupId: activeGroupId ?? "" },
    {
      enabled: activeGroupId !== "",
      refetchOnWindowFocus: false,
    }
  );

  const transformDataToFilterOptions = useCallback(
    (data: Array<Record<string, any>>, key: string) =>
      data.map((item) => ({ id: item.id, label: item[key] })),
    []
  );

  // Memoize transformed data
  const transformedListings = useMemo(() => {
    const rawListings =
      listingsData?.listings.listings ||
      groupListingsData?.brandGroup?.listings ||
      [];
    return transformDataToFilterOptions(rawListings, "name");
  }, [listingsData, groupListingsData, transformDataToFilterOptions]);

  const transformedLocations = useMemo(() => {
    const rawLocations =
      locationsData?.brand?.locations.locations ||
      groupLocationsData?.brandGroup?.locations ||
      [];
    return transformDataToFilterOptions(rawLocations, "name");
  }, [locationsData, groupLocationsData, transformDataToFilterOptions]);

  // Update state when data changes
  useEffect(() => {
    setListings(transformedListings);
    // Only set all listings if none are selected (no URL params)
    if (initialFilters.selectedListings.length === 0) {
      setSelectedListings(transformedListings.map((listing) => listing.id));
    } else {
      // Validate URL params against loaded data
      const validListingIds = transformedListings.map((l) => l.id);
      const validSelected = initialFilters.selectedListings.filter((id) =>
        validListingIds.includes(id)
      );
      setSelectedListings(
        validSelected.length > 0 ? validSelected : validListingIds
      );
    }
  }, [transformedListings, initialFilters.selectedListings]);

  useEffect(() => {
    setLocations(transformedLocations);
    // Only set all locations if none are selected (no URL params)
    if (initialFilters.selectedLocations.length === 0) {
      setSelectedLocations(transformedLocations.map((location) => location.id));
    } else {
      // Validate URL params against loaded data
      const validLocationIds = transformedLocations.map((l) => l.id);
      const validSelected = initialFilters.selectedLocations.filter((id) =>
        validLocationIds.includes(id)
      );
      setSelectedLocations(
        validSelected.length > 0 ? validSelected : validLocationIds
      );
    }
  }, [transformedLocations, initialFilters.selectedLocations]);

  // Update URL when filters change
  useEffect(() => {
    const queryString = serializeFilters(
      startDate,
      endDate,
      // searchQuery,
      selectedListings,
      selectedLocations,
      selectedContentType,
      listings,
      locations
    );

    const newUrl = queryString
      ? `${location.pathname}?${queryString}`
      : location.pathname;

    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  }, [
    startDate,
    endDate,
    // searchQuery,
    selectedListings,
    selectedLocations,
    selectedContentType,
    listings,
    locations,
    history,
    location.pathname,
    location.search,
  ]);

  return (
    <Wrap>
      <HeaderWrap>
        <H1 margin="0 l 0 0">Content</H1>
        <FilterWrap gap="s" align="center">
          <DateRangePicker
            initialStartDate={startDate}
            initialEndDate={endDate}
            onChange={handleDateChange}
            tooltip="Based on the content post date"
            onOpenChange={setDatePickerOpen}
            open={datePickerOpen}
          />
          <Dropdown
            options={listings.map((l) => ({ label: l.label, value: l.id }))}
            selectedOptions={selectedListings}
            setSelectedOptions={setSelectedListings}
            renderLabel={() => {
              if (selectedListings.length === 0) return "No Campaigns";
              if (selectedListings.length === listings.length)
                return "All Campaigns";
              if (selectedListings.length === 1) {
                const selected = listings.find(
                  (l) => l.id === selectedListings[0]
                );
                return selected?.label || "Campaign";
              }
              return `${selectedListings.length} Campaigns`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<ListingsIcon colorPreset="text" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          <Dropdown
            options={locations.map((l) => ({ label: l.label, value: l.id }))}
            selectedOptions={selectedLocations}
            setSelectedOptions={setSelectedLocations}
            renderLabel={() => {
              if (selectedLocations.length === 0) return "No Locations";
              if (selectedLocations.length === locations.length)
                return "All Locations";
              if (selectedLocations.length === 1) {
                const selected = locations.find(
                  (l) => l.id === selectedLocations[0]
                );
                return selected?.label || "Location";
              }
              return `${selectedLocations.length} Locations`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<LocationsIcon colorPreset="text" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          <Dropdown
            options={contentTypes.map((c) => ({ label: c.label, value: c.id }))}
            selectedOptions={selectedContentType}
            setSelectedOptions={(selected) =>
              setSelectedContentType(selected as BookingPostType[])
            }
            renderLabel={() => {
              if (selectedContentType.length === 0) return "No Content Types";
              if (selectedContentType.length === contentTypes.length)
                return "All Content";
              if (selectedContentType.length === 1) {
                const selected = contentTypes.find(
                  (c) => c.id === selectedContentType[0]
                );
                return selected?.label || "Content Type";
              }
              return `${selectedContentType.length} Content Types`;
            }}
            selectionMode="multiple"
            size="xs"
            showSelectAll
            anchor="bottom end"
            icon={<ContentIcon colorPreset="text" width={17} />}
            onDropdownClick={() => {
              if (datePickerOpen) setDatePickerOpen(false);
            }}
          />
          {/* TODO: Add search filter back in */}
          {/* <SearchFilter
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          /> */}
        </FilterWrap>
      </HeaderWrap>
      <Reports
        activeBrandId={activeBrandId ?? undefined}
        startDate={startDate}
        endDate={endDate}
        selectedListings={optimizedListings}
        selectedLocations={optimizedLocations}
        selectedContentType={optimizedContentTypes}
        // searchQuery={searchQuery}
      />
    </Wrap>
  );
};

interface ReportsProps {
  activeBrandId?: string;
  startDate: Date | null;
  endDate: Date | null;
  selectedListings: string[] | undefined;
  selectedLocations: string[] | undefined;
  selectedContentType: BookingPostType[] | undefined;
  // searchQuery: string;
}

function Reports(props: ReportsProps) {
  const client = useGqlClient();
  const activeBrandId = useSelector(authSelectors.activeBrandId);

  const [sortProperty] = useState<BookingPostsSortBy>(
    BookingPostsSortBy.BookingPostsSortByReach
  );

  const serializedFilters = JSON.stringify({
    startDate: props.startDate ? getUnixTime(props.startDate) : undefined,
    endDate: props.endDate ? getUnixTime(props.endDate) : undefined,
    listingIDs: props.selectedListings,
    locationIDs: props.selectedLocations,
    contentTypes: props.selectedContentType,
  });

  const fetchPosts = async ({ pageParam = "" }) => {
    return client.request<BrandContentQuery>(BrandContentDocument, {
      cursor: pageParam,
      filters: {
        startDate: props.startDate ? getUnixTime(props.startDate) : undefined,
        endDate: props.endDate ? getUnixTime(props.endDate) : undefined,
        listingIDs: props.selectedListings,
        sortBy: sortProperty,
        locationIDs: props.selectedLocations,
        contentTypes: props.selectedContentType,
      },
      brandID:
        props.activeBrandId && props.activeBrandId !== ""
          ? props.activeBrandId
          : undefined,
    });
  };

  const { data, isLoading, error, fetchNextPage, hasNextPage } =
    useInfiniteQuery(
      [queryKeys.brandContent, props.activeBrandId, serializedFilters],
      fetchPosts,
      {
        getNextPageParam: (lastPage) =>
          lastPage.bookingPosts.cursor || undefined,
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: false,
      }
    );

  const { data: socialData } = useGetBrandSocialsQuery(
    client,
    {
      id: activeBrandId ? activeBrandId : "",
    },
    {
      enabled: activeBrandId !== "",
    }
  );

  const observer = useRef<IntersectionObserver | null>(null);

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasNextPage, fetchNextPage]
  );

  if (error) {
    return (
      <Flex align="center" justify="center" direction="column">
        <Text>Something went wrong</Text>
      </Flex>
    );
  }

  const bookingPosts =
    data?.pages.flatMap((page) => page.bookingPosts.bookingPosts) || [];

  if (isLoading) return <Loading />;

  return (
    <>
      {bookingPosts.length > 0 ? (
        <MasonryList>
          {bookingPosts.map((bp, index) => {
            return (
              <BookingPost
                ref={index === bookingPosts.length - 1 ? lastElementRef : null}
                key={bp.id}
                name={
                  bp.postType === BookingPostType.BookingPostTypeTiktok &&
                  bp.creator.tikTok
                    ? bp.creator.tikTok.displayName
                    : bp.creator.instagram
                    ? bp.creator.instagram.name
                    : ""
                }
                username={
                  bp.postType === BookingPostType.BookingPostTypeTiktok &&
                  bp.creator.tikTok
                    ? bp.creator.tikTok.username
                    : bp.creator.instagram
                    ? bp.creator.instagram.username
                    : ""
                }
                mediaUrl={bp.mediaUrl}
                // metricValue={bp.value}
                thumbnailUrl={bp.thumbnailUrl}
                caption={bp.mediaCaption}
                mediaType={bp.mediaType}
                postType={bp.postType}
                id={bp.id}
                maxWidth={600}
              />
            );
          })}
        </MasonryList>
      ) : (
        <Flex
          style={{ marginTop: "120px" }}
          margin="0"
          direction="column"
          align="center"
        >
          <Text isCompact weight="semi" margin="0" colorPreset="secondary">
            No data for this date range
          </Text>
          {socialData &&
          socialData.brand &&
          !socialData.brand.instagram &&
          !socialData.brand.tikTok ? (
            <Flex margin="m 0 0">
              <CTA to="/b/settings" type="primary" size="xs">
                Connect your Socials
              </CTA>
            </Flex>
          ) : null}
        </Flex>
      )}
    </>
  );
}

const FilterWrap = styled(Flex)`
  display: flex;
  flex-direction: row;
  gap: ${(p) => p.theme.spacing.s};
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
`;
